import moment from "moment";

/**
 * Toggle server-side sort for `get_rfis` (My RFIs) vs client-side sort on the current page only.
 */
export const MY_RFIS_USE_SERVER_SORT = true;

/**
 * Normalized row for the RFI History-style table (Innovation Lab).
 *
 * When the backend adds new fields, extend `mapMyRfiItemToGridRow` to read them
 * from `item` (e.g. item.location, item.gxp, item.risk_score).
 */
/**
 * Label for the Type column: API `type` when present, else legacy `is_global_template`.
 */
export function getRfiTypeLabelFromItem(item) {
  const fromApi = item?.type ?? item?.Type;
  if (fromApi != null && String(fromApi).trim() !== "") {
    return String(fromApi).trim();
  }
  return Boolean(item?.is_global_template) ? "Diligent VQQ" : "Custom VQQ";
}

/** True when UI should use the "Diligent" (purple) badge style for this label. */
export function rfiTypeLabelLooksDiligent(typeLabel) {
  if (!typeLabel) return false;
  return /\bdiligent\b/i.test(String(typeLabel));
}

/** Toolbar Type filter: diligent vs custom, aligned with `getRfiTypeLabelFromItem`. */
export function isDiligentRfiItemForTypeFilter(item) {
  const label = getRfiTypeLabelFromItem(item).toLowerCase();
  if (label.includes("diligent")) return true;
  if (label.includes("custom")) return false;
  return Boolean(item?.is_global_template);
}

export const MY_RFI_GRID_EMPTY = {
  vendor: "",
  templateName: "",
  typeLabel: "",
  gxp: "",
  services: "",
  location: "",
  riskSummary: "",
  status: "",
  requestDateRaw: null,
  releaseDateRaw: null,
  requestDateDisplay: "",
  releaseDateDisplay: "",
  cycleTime: "",
};

/**
 * Cycle time string from the API (`cycle_time` / `cycleTime`). Empty when missing — do not derive from dates.
 * @param {object|null|undefined} item
 */
export function getRfiCycleTimeFromApi(item) {
  if (!item) return "";
  const v = item.cycle_time ?? item.cycleTime ?? item.CycleTime;
  if (v == null || v === "") return "";
  return String(v).trim();
}

/**
 * @param {object|null|undefined} item - Raw row from the get_rfis API
 * @returns {typeof MY_RFI_GRID_EMPTY & { raw: object, cycleTime: string }}
 */
export function mapMyRfiItemToGridRow(item) {
  if (!item) {
    return { ...MY_RFI_GRID_EMPTY, raw: {} };
  }

  const templateName = item.name ?? "";
  const typeLabel = getRfiTypeLabelFromItem(item);

  // Prefer API field when present; otherwise derive from template name
  const gxpFromApi = item.gxp ?? item.gxp_label ?? item.gxpLabel ?? "";
  const gxpFromTemplate = extractGxpFromTemplateName(templateName);
  const gxp = (gxpFromApi || gxpFromTemplate || "").trim();

  const location =
    item.location ??
    item.vendor_location ??
    item.vendorLocation ??
    item.city_state_country ??
    "";

  const riskSummary = formatRiskSummary(item);

  const requestDateRaw = item.requestDate ?? null;
  const releaseDateRaw = item.releaseDate ?? item.releasedDate ?? null;

  const servicesRaw = item.services ?? item.Services;
  const servicesDisplay = normalizeRfiServicesFieldToString(servicesRaw);

  return {
    vendor: item.vendorName ?? "",
    templateName,
    typeLabel,
    gxp,
    services: servicesDisplay,
    location: typeof location === "string" ? location : "",
    riskSummary,
    status: item.status ?? "",
    requestDateRaw,
    releaseDateRaw,
    requestDateDisplay: formatGridDate(requestDateRaw),
    releaseDateDisplay: formatGridDate(releaseDateRaw),
    cycleTime: getRfiCycleTimeFromApi(item),
    raw: item,
  };
}

/**
 * RFI list APIs may return `services` as a string, array of labels, or nested objects.
 * Coerce to a single display/sort string (same logic for grid + export + sort).
 */
export function normalizeRfiServicesFieldToString(raw) {
  if (raw == null || raw === "") return "";
  if (typeof raw === "string") return raw.trim();
  if (typeof raw === "number" || typeof raw === "boolean") return String(raw);
  if (Array.isArray(raw)) {
    return raw
      .map((x) => {
        if (x == null) return "";
        if (typeof x === "string" || typeof x === "number") return String(x).trim();
        if (typeof x === "object") {
          return String(
            x.name ?? x.label ?? x.services ?? x.rfi_name ?? ""
          ).trim();
        }
        return String(x);
      })
      .filter(Boolean)
      .join(", ");
  }
  if (typeof raw === "object") {
    return String(
      raw.name ?? raw.label ?? raw.services ?? raw.rfi_name ?? ""
    ).trim();
  }
  return String(raw).trim();
}

/** Lowercase string for comparing RFI service/template column rows when sorting. */
export function getRfiServicesSortComparable(item) {
  if (!item) return "";
  const raw = item.services ?? item.Services;
  return normalizeRfiServicesFieldToString(raw).toLowerCase();
}

function extractGxpFromTemplateName(name) {
  if (!name || typeof name !== "string") return "";
  const m = name.match(/\b(GCP|GCLP|GLP|GMP|GVP|GDP|GAMP)\b/i);
  return m ? m[1].toUpperCase() : "";
}

function formatRiskSummary(item) {
  const rs = item.risk_summary ?? item.riskSummary;
  if (rs != null && String(rs).trim() !== "") return String(rs);

  const score = item.risk_score ?? item.riskScore;
  const level = item.risk_level ?? item.riskLevel;
  if (score != null && level != null) return `${level} (${score}%)`;
  if (score != null) return String(score);
  if (level != null) return String(level);
  return "";
}

/** Short dates aligned with the Innovation Lab grid (M-D-YY) */
export function formatGridDate(value) {
  if (value == null || value === "") return "";
  try {
    const m = moment.utc(value);
    if (!m.isValid()) return "";
    return m.format("M-D-YY");
  } catch {
    return "";
  }
}

export function computeCycleTime(requestDate, releaseDate) {
  if (!requestDate || !releaseDate) return "";
  try {
    const a = new Date(requestDate);
    const b = new Date(releaseDate);
    if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return "";
    const diff = Math.ceil(Math.abs(b - a) / (1000 * 60 * 60 * 24));
    return `${diff} days`;
  } catch {
    return "";
  }
}

/** Sort key extraction from a raw API row (same keys as handleSort on the page) */
export function getSortComparableValue(item, sortKey) {
  if (!item) return null;
  const row = mapMyRfiItemToGridRow(item);

  switch (sortKey) {
    case "sponsor":
      return (item.sponsorName || "").toLowerCase();
    case "vendor":
      return (item.vendorName || "").toLowerCase();
    case "template":
      // "Template" column shows service categories (`services`); sort matches visible badges.
      return getRfiServicesSortComparable(item);
    case "type":
      return getRfiTypeLabelFromItem(item).toLowerCase();
    case "gxp":
      return (row.gxp || "zzz").toLowerCase();
    case "services":
      return getRfiServicesSortComparable(item);
    case "location":
      return (row.location || "zzz").toLowerCase();
    case "risk":
      return parseRiskSortValue(item);
    case "status":
      return (item.status || "").toLowerCase();
    case "requestDate":
      return item.requestDate ? new Date(item.requestDate).getTime() : 0;
    case "releasedDate":
      return item.releaseDate || item.releasedDate
        ? new Date(item.releaseDate || item.releasedDate).getTime()
        : 0;
    case "cycleTime": {
      const s = getRfiCycleTimeFromApi(item);
      if (!s) return "";
      const n = parseInt(String(s).replace(/\D/g, ""), 10);
      return Number.isNaN(n) ? s.toLowerCase() : n;
    }
    default:
      return "";
  }
}

/**
 * Maps RfiGridTable column keys to `sort_by` values for `get_rfis`.
 * Align names with the backend contract when enabling server-side sort.
 */
/** Columns not listed here are not sortable on the server (see RfiGridTable). */
export const MY_RFI_API_ORDER_BY_FIELD = {
  vendor: "vendor_name",
  /** Template column shows service categories; server sort must use `services`, not questionnaire title. */
  template: "services",
  gxp: "gxp",
  location: "location",
  risk: "risk_summary",
  status: "status",
  requestDate: "request_date",
  releasedDate: "release_date",
  cycleTime: "cycle_time",
};

/**
 * Maps sort state to API `order_by` / `sort_direction` using a column-key → API field map.
 */
export function sortConfigToApiSortParamsWithFieldMap(fieldMap, sortConfig) {
  const keys = Object.keys(sortConfig || {});
  if (keys.length === 0) {
    return { orderBy: null, sortDirection: null };
  }
  const columnKey = keys[0];
  if (columnKey === "actions") {
    return { orderBy: null, sortDirection: null };
  }
  const orderBy = fieldMap[columnKey];
  if (!orderBy) {
    return { orderBy: null, sortDirection: null };
  }
  const direction = sortConfig[columnKey]?.direction;
  const sortDirection = direction === "asc" ? "asc" : "desc";
  return { orderBy, sortDirection };
}

/**
 * @param {Record<string, { direction?: string }>} sortConfig — single active column (same shape as table state)
 * @returns {{ orderBy: string | null, sortDirection: 'asc' | 'desc' | null }}
 */
export function sortConfigToApiSortParams(sortConfig) {
  return sortConfigToApiSortParamsWithFieldMap(
    MY_RFI_API_ORDER_BY_FIELD,
    sortConfig
  );
}

function parseRiskSortValue(item) {
  const n = item.risk_score ?? item.riskScore;
  if (typeof n === "number") return n;
  if (n != null && !Number.isNaN(Number(n))) return Number(n);
  return -1;
}

function naturalSortCompare(a, b) {
  if (!a && b) return -1;
  if (a && !b) return 1;
  if (!a && !b) return 0;
  const aStr = String(a).trim();
  const bStr = String(b).trim();
  const aParts = aStr.split(/(\d+)/).filter(Boolean);
  const bParts = bStr.split(/(\d+)/).filter(Boolean);
  const minLength = Math.min(aParts.length, bParts.length);
  for (let i = 0; i < minLength; i++) {
    if (!isNaN(aParts[i]) && !isNaN(bParts[i])) {
      const numA = parseInt(aParts[i], 10);
      const numB = parseInt(bParts[i], 10);
      if (numA !== numB) return numA - numB;
    } else if (aParts[i] !== bParts[i]) {
      return aParts[i].localeCompare(bParts[i]);
    }
  }
  return aParts.length - bParts.length;
}

/**
 * Sorts My RFIs API rows using sortConfig (single active column).
 * Used by the page and the RFI table component.
 */
/** GxP abbreviations used in toolbar filters (subset of template-name extraction). */
export const MY_RFI_GXP_FILTER_OPTIONS = [
  { value: "GCP", label: "GCP" },
  { value: "GCLP", label: "GCLP" },
  { value: "GLP", label: "GLP" },
  { value: "GMP", label: "GMP" },
  { value: "GVP", label: "GVP" },
  { value: "GDP", label: "GDP" },
  { value: "GAMP", label: "GAMP" },
];

/**
 * Client-side filters for fields not covered by get_rfis (Type, GxP).
 * When any of these are active, filtering applies to the current page payload only.
 */
export function filterMyRfiItemsByClientFilters(items, selectedIds) {
  if (!items?.length) return [];
  const typeFilters = selectedIds?.type || [];
  const gxpFilters = selectedIds?.gxp || [];
  if (typeFilters.length === 0 && gxpFilters.length === 0) return items;

  return items.filter((item) => {
    if (typeFilters.length > 0) {
      const isDiligent = isDiligentRfiItemForTypeFilter(item);
      const ok = typeFilters.some((t) => {
        if (t === "diligent") return isDiligent;
        if (t === "custom") return !isDiligent;
        return false;
      });
      if (!ok) return false;
    }
    if (gxpFilters.length > 0) {
      const row = mapMyRfiItemToGridRow(item);
      const gxpVal = (row.gxp || "").toUpperCase();
      const ok = gxpFilters.some((code) => {
        const c = String(code).toUpperCase();
        return gxpVal === c || gxpVal.includes(c);
      });
      if (!ok) return false;
    }
    return true;
  });
}

export function sortMyRfiItems(items, sortConfig) {
  if (!items?.length) return [];
  const copy = [...items];
  const keys = Object.keys(sortConfig || {});
  if (keys.length === 0) return copy;
  const key = keys[0];
  const { direction } = sortConfig[key];
  const mult = direction === "asc" ? 1 : -1;

  if (key === "sponsor") {
    return copy.sort((a, b) => {
      const result = naturalSortCompare(
        (a.sponsorName || "").toLowerCase(),
        (b.sponsorName || "").toLowerCase()
      );
      return result * mult;
    });
  }

  if (key === "vendor") {
    return copy.sort((a, b) => {
      const result = naturalSortCompare(
        (a.vendorName || "").toLowerCase(),
        (b.vendorName || "").toLowerCase()
      );
      return result * mult;
    });
  }

  if (key === "services" || key === "template") {
    return copy.sort((a, b) => {
      const result = naturalSortCompare(
        getRfiServicesSortComparable(a),
        getRfiServicesSortComparable(b)
      );
      return result * mult;
    });
  }

  if (key === "cycleTime") {
    return copy.sort((a, b) => {
      const valueA = getRfiCycleTimeFromApi(a);
      const valueB = getRfiCycleTimeFromApi(b);
      const isEmptyA = !valueA;
      const isEmptyB = !valueB;
      if (isEmptyA && !isEmptyB) return direction === "asc" ? 1 : -1;
      if (!isEmptyA && isEmptyB) return direction === "asc" ? -1 : 1;
      if (isEmptyA && isEmptyB) return 0;
      const result = naturalSortCompare(
        valueA.toLowerCase(),
        valueB.toLowerCase()
      );
      return direction === "asc" ? result : -result;
    });
  }

  return copy.sort((a, b) => {
    const va = getSortComparableValue(a, key);
    const vb = getSortComparableValue(b, key);
    if (va === vb) return 0;
    if (typeof va === "number" && typeof vb === "number") {
      return va < vb ? -mult : mult;
    }
    const sa = String(va ?? "");
    const sb = String(vb ?? "");
    if (sa < sb) return -mult;
    if (sa > sb) return mult;
    return 0;
  });
}

export const MY_RFI_EXPORT_HEADERS = [
  "Vendor",
  "Type",
  "GxP",
  "Template",
  "Location",
  "Risk Summary",
  "Status",
  "Request Date",
  "Release Date",
  "Cycle Time",
];

export function mapGridRowToExportRecord(row) {
  return {
    Vendor: row.vendor,
    Type: row.typeLabel,
    "GxP": row.gxp,
    Template: row.services,
    Location: row.location || "",
    "Risk Summary": row.riskSummary || "",
    Status: row.status,
    "Request Date": row.requestDateDisplay || "",
    "Release Date": row.releaseDateDisplay || "",
    "Cycle Time": row.cycleTime || "",
  };
}
